import { useEffect } from 'react';

import { QuickQuoteResponse, RouteSegment } from 'lib/api/getQuickQuote';
import { Maybe } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';
import { GenericSuggestion } from 'types/suggestions/CoreSuggestions';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';

import { DistanceSource } from '../QuickQuoteForm';
import {
  quoteSourceForLegMetadata,
  resolveQuoteMileageSource,
} from '../utils/mileageSource';

/**
 * Hook that calculates and sets distance from various sources
 * Priority: Suggestion distance -> DAT multi-stop -> Max distance from quotes
 */
export const useDistanceCalculation = (
  quoteResponse: Maybe<QuickQuoteResponse>,
  quoteNotConfident: boolean,
  clickedSuggestion: Maybe<GenericSuggestion>,
  distanceSource: Maybe<DistanceSource>,
  setMaxDistance: (distance: number) => void,
  setDistanceSource: (source: Maybe<DistanceSource>) => void,
  setRouteDetails: (
    details: Maybe<{ source: DistanceSource; legs: RouteSegment[] }>
  ) => void,
  setStopFee: (fee: number) => void,
  setIntermediateStopCount: (count: number) => void,
  lastAppliedStopFeeRef: React.MutableRefObject<number>
) => {
  useEffect(() => {
    const isQuoteResponseValid = quoteResponse;
    const isQuoteResponseEmpty = quoteResponse?.quotes?.length === 0;

    const isQQSuggestionWithDistance =
      clickedSuggestion?.pipeline === SuggestionPipelines.QuickQuote &&
      clickedSuggestion?.suggested?.distanceMiles;
    const isMultiStopQuote =
      quoteResponse?.stops?.length && quoteResponse.stops.length > 2;

    if (!isQuoteResponseValid && !quoteNotConfident) {
      return;
    }

    let resolvedDistanceSource: Maybe<DistanceSource> = distanceSource;

    // First use parsed distance from suggestion/bidding portal if available
    if (isQQSuggestionWithDistance) {
      setMaxDistance(clickedSuggestion.suggested.distanceMiles ?? 0);
      resolvedDistanceSource = clickedSuggestion.source as DistanceSource;
      setDistanceSource(resolvedDistanceSource);
    } else if (isMultiStopQuote) {
      // Otherwise, use the total distance from DAT if available.
      // (Greenscreens natively supports multi-stop but does not return the per-leg distance,
      // so we prefer total + leg distances from DAT)
      const datMultiStopQuote = quoteResponse.quotes?.find(
        (q) => q.source === Quoting.DAT
      );

      if (datMultiStopQuote) {
        setMaxDistance(datMultiStopQuote.distance);
        resolvedDistanceSource = resolveQuoteMileageSource(datMultiStopQuote);
        setDistanceSource(resolvedDistanceSource);
      }
    } else if (isQuoteResponseValid && !isQuoteResponseEmpty) {
      const maxDistanceQuote = quoteResponse.quotes?.reduce((max, current) =>
        current.distance > max.distance ? current : max
      );

      setMaxDistance(maxDistanceQuote.distance);
      resolvedDistanceSource = resolveQuoteMileageSource(maxDistanceQuote);
      setDistanceSource(resolvedDistanceSource);
    }

    const legMatchSource = quoteSourceForLegMetadata(resolvedDistanceSource);

    // Set route details
    const quoteWithLegs =
      quoteResponse?.quotes.find(
        (q) => q.metadata?.legs && q.source === legMatchSource
      ) || quoteResponse?.quotes.find((q) => q.metadata?.legs);

    if (quoteWithLegs?.metadata?.legs) {
      setRouteDetails({
        source: quoteWithLegs.source as DistanceSource,
        legs: quoteWithLegs.metadata.legs,
      });
    }

    const quoteWithStopFee = quoteResponse?.quotes.find(
      (q) =>
        q.metadata?.stopFeeUSD != null || q.metadata?.stopFeeUSDMedium != null
    );
    const stopFeeValue =
      quoteWithStopFee?.metadata?.stopFeeUSD ??
      quoteWithStopFee?.metadata?.stopFeeUSDMedium;
    if (stopFeeValue != null) {
      setStopFee(stopFeeValue);
      lastAppliedStopFeeRef.current = stopFeeValue;
    }

    const count = Math.max((quoteResponse?.stops?.length || 0) - 2, 0);
    setIntermediateStopCount(count);
  }, [
    quoteResponse,
    quoteNotConfident,
    clickedSuggestion,
    distanceSource,
    setMaxDistance,
    setDistanceSource,
    setRouteDetails,
    setStopFee,
    setIntermediateStopCount,
    lastAppliedStopFeeRef,
  ]);
};
